import { z } from 'zod';

export type OnboardingEventType =
  | 'Onboarding_WelcomeCTAClicked'
  | 'Onboarding_WelcomeOptOutClicked'
  | 'Onboarding_Home_OtherProductsCreateProductClicked'
  | 'Onboarding_Home_ChecklistItemClicked'
  | 'Onboarding_Home_ChecklistCompleteSubmitProductClicked'
  | 'Onboarding_Home_ActiveProductCardCreateFirstProductClicked'
  | 'DirectSeller_SignupLinkGenerated'
  | 'DirectSeller_WelcomeCTAClicked'
  | 'DirectSeller_WizardStarted'
  | 'DirectSeller_StepViewed'
  | 'DirectSeller_WizardClosed'
  | 'DirectSeller_StripeConnected'
  | 'DirectSeller_Connection_ProvidersSelected'
  | 'DirectSeller_Connection_ProvidersConnected'
  | 'DirectSeller_Connection_ProvidersError'
  | 'DirectSeller_Connection_SEPAChosen'
  | 'DirectSeller_Connection_AvgPriceChosen'
  | 'DirectSeller_Connection_SalesVolumeChosen'
  | 'DirectSeller_Connection_ProductsOver10KChosen'
  | 'DirectSeller_LimitGuidanceShown'
  | 'DirectSeller_LimitEmailCopied'
  | 'DirectSeller_Email_ProviderSelected'
  | 'DirectSeller_Email_PostmarkConfigured'
  | 'DirectSeller_Email_SMTPConfigured'
  | 'DirectSeller_LegalDraftGenerated'
  | 'DirectSeller_LegalDocumentsSaved'
  | 'DirectSeller_BusinessInfo_Success'
  | 'DirectSeller_BusinessInfo_Error'
  | 'DirectSeller_Documents_Completed'
  | 'DirectSeller_SetupCompleted';

// Onboarding event payload schemas
export const OnboardingEventBaseSchema = z.object({
  source: z.string(),
  eventContext: z.string().optional(),
  feature: z.string(),
  feature_version: z.string(),
  page: z.string(),
});

export const Onboarding_WelcomeCTAClickedPayloadSchema = OnboardingEventBaseSchema.extend({});

export const Onboarding_WelcomeOptOutClickedPayloadSchema = OnboardingEventBaseSchema.extend({});

export const Onboarding_Home_OtherProductsCreateProductClickedPayloadSchema = OnboardingEventBaseSchema.extend({});

export const Onboarding_Home_ChecklistItemClickedPayloadSchema = OnboardingEventBaseSchema.extend({
  checklistItemKey: z.string(),
});

export const Onboarding_Home_ChecklistCompleteSubmitProductClickedPayloadSchema = OnboardingEventBaseSchema.extend({});

export const Onboarding_Home_ActiveProductCardCreateFirstProductClickedPayloadSchema = OnboardingEventBaseSchema.extend(
  {}
);

// DirectSeller event schemas
export const DirectSeller_SignupLinkGeneratedPayloadSchema = OnboardingEventBaseSchema.extend({
  email: z.string().email(),
  language: z.string(),
  oneTimeKey: z.string(),
  link: z.string().url(),
});

export const DirectSeller_WelcomeCTAClickedPayloadSchema = OnboardingEventBaseSchema.extend({});

export const DirectSeller_WizardStartedPayloadSchema = OnboardingEventBaseSchema.extend({
  entry_point: z.string(), // 'post_signup', 'return_login', 'account_area'
  started_at: z.string(), // ISO 8601 timestamp
});

export const DirectSeller_StepViewedPayloadSchema = OnboardingEventBaseSchema.extend({
  step_id: z.string(),
  step_name: z.string(),
  is_first_visit: z.boolean(),
});

export const DirectSeller_WizardClosedPayloadSchema = OnboardingEventBaseSchema.extend({
  last_step_id: z.string(),
  last_step_name: z.string(),
  completed_steps: z.array(z.string()),
});

export const DirectSeller_StripeConnectedPayloadSchema = OnboardingEventBaseSchema.extend({});

export const DirectSeller_Connection_ProvidersSelectedPayloadSchema = OnboardingEventBaseSchema.extend({
  providers: z.array(z.string()),
});

export const DirectSeller_Connection_ProvidersConnectedPayloadSchema = OnboardingEventBaseSchema.extend({
  providers: z.array(z.string()),
  stripe_connected: z.boolean(),
});

export const DirectSeller_Connection_ProvidersErrorPayloadSchema = OnboardingEventBaseSchema.extend({
  message: z.string(),
});

export const DirectSeller_Connection_SEPAChosenPayloadSchema = OnboardingEventBaseSchema.extend({
  sepa: z.boolean(),
});

export const DirectSeller_Connection_AvgPriceChosenPayloadSchema = OnboardingEventBaseSchema.extend({
  avg_price_option: z.string(),
});

export const DirectSeller_Connection_SalesVolumeChosenPayloadSchema = OnboardingEventBaseSchema.extend({
  sales_volume_option: z.string(),
});

export const DirectSeller_Connection_ProductsOver10KChosenPayloadSchema = OnboardingEventBaseSchema.extend({
  products_over_10k: z.string(),
});

export const DirectSeller_LimitGuidanceShownPayloadSchema = OnboardingEventBaseSchema.extend({
  products_over_10k: z.boolean(),
});

export const DirectSeller_LimitEmailCopiedPayloadSchema = OnboardingEventBaseSchema.extend({});

export const DirectSeller_Email_ProviderSelectedPayloadSchema = OnboardingEventBaseSchema.extend({
  provider: z.string(),
});

export const DirectSeller_Email_PostmarkConfiguredPayloadSchema = OnboardingEventBaseSchema.extend({});

export const DirectSeller_Email_SMTPConfiguredPayloadSchema = OnboardingEventBaseSchema.extend({});

export const DirectSeller_LegalDraftGeneratedPayloadSchema = OnboardingEventBaseSchema.extend({
  document_type: z.string(), // 'imprint', 'terms', 'privacy', 'cancellation'
});

export const DirectSeller_LegalDocumentsSavedPayloadSchema = OnboardingEventBaseSchema.extend({
  all_documents_complete: z.boolean(),
});

export const DirectSeller_BusinessInfo_SuccessPayloadSchema = OnboardingEventBaseSchema.extend({});

export const DirectSeller_BusinessInfo_ErrorPayloadSchema = OnboardingEventBaseSchema.extend({});

export const DirectSeller_Documents_CompletedPayloadSchema = OnboardingEventBaseSchema.extend({});

export const DirectSeller_SetupCompletedPayloadSchema = OnboardingEventBaseSchema.extend({
  started_at: z.string(), // ISO 8601 timestamp
  completed_at: z.string(), // ISO 8601 timestamp
});

export type OnboardingEventTypePayloadMap = {
  Onboarding_WelcomeCTAClicked: z.infer<typeof Onboarding_WelcomeCTAClickedPayloadSchema>;
  Onboarding_WelcomeOptOutClicked: z.infer<typeof Onboarding_WelcomeOptOutClickedPayloadSchema>;
  Onboarding_Home_OtherProductsCreateProductClicked: z.infer<
    typeof Onboarding_Home_OtherProductsCreateProductClickedPayloadSchema
  >;
  Onboarding_Home_ChecklistItemClicked: z.infer<typeof Onboarding_Home_ChecklistItemClickedPayloadSchema>;
  Onboarding_Home_ChecklistCompleteSubmitProductClicked: z.infer<
    typeof Onboarding_Home_ChecklistCompleteSubmitProductClickedPayloadSchema
  >;
  Onboarding_Home_ActiveProductCardCreateFirstProductClicked: z.infer<
    typeof Onboarding_Home_ActiveProductCardCreateFirstProductClickedPayloadSchema
  >;
  DirectSeller_SignupLinkGenerated: z.infer<typeof DirectSeller_SignupLinkGeneratedPayloadSchema>;
  DirectSeller_WelcomeCTAClicked: z.infer<typeof DirectSeller_WelcomeCTAClickedPayloadSchema>;
  DirectSeller_WizardStarted: z.infer<typeof DirectSeller_WizardStartedPayloadSchema>;
  DirectSeller_StepViewed: z.infer<typeof DirectSeller_StepViewedPayloadSchema>;
  DirectSeller_WizardClosed: z.infer<typeof DirectSeller_WizardClosedPayloadSchema>;
  DirectSeller_StripeConnected: z.infer<typeof DirectSeller_StripeConnectedPayloadSchema>;
  DirectSeller_Connection_ProvidersSelected: z.infer<typeof DirectSeller_Connection_ProvidersSelectedPayloadSchema>;
  DirectSeller_Connection_ProvidersConnected: z.infer<typeof DirectSeller_Connection_ProvidersConnectedPayloadSchema>;
  DirectSeller_Connection_ProvidersError: z.infer<typeof DirectSeller_Connection_ProvidersErrorPayloadSchema>;
  DirectSeller_Connection_SEPAChosen: z.infer<typeof DirectSeller_Connection_SEPAChosenPayloadSchema>;
  DirectSeller_Connection_AvgPriceChosen: z.infer<typeof DirectSeller_Connection_AvgPriceChosenPayloadSchema>;
  DirectSeller_Connection_SalesVolumeChosen: z.infer<typeof DirectSeller_Connection_SalesVolumeChosenPayloadSchema>;
  DirectSeller_Connection_ProductsOver10KChosen: z.infer<
    typeof DirectSeller_Connection_ProductsOver10KChosenPayloadSchema
  >;
  DirectSeller_LimitGuidanceShown: z.infer<typeof DirectSeller_LimitGuidanceShownPayloadSchema>;
  DirectSeller_LimitEmailCopied: z.infer<typeof DirectSeller_LimitEmailCopiedPayloadSchema>;
  DirectSeller_Email_ProviderSelected: z.infer<typeof DirectSeller_Email_ProviderSelectedPayloadSchema>;
  DirectSeller_Email_PostmarkConfigured: z.infer<typeof DirectSeller_Email_PostmarkConfiguredPayloadSchema>;
  DirectSeller_Email_SMTPConfigured: z.infer<typeof DirectSeller_Email_SMTPConfiguredPayloadSchema>;
  DirectSeller_LegalDraftGenerated: z.infer<typeof DirectSeller_LegalDraftGeneratedPayloadSchema>;
  DirectSeller_LegalDocumentsSaved: z.infer<typeof DirectSeller_LegalDocumentsSavedPayloadSchema>;
  DirectSeller_BusinessInfo_Success: z.infer<typeof DirectSeller_BusinessInfo_SuccessPayloadSchema>;
  DirectSeller_BusinessInfo_Error: z.infer<typeof DirectSeller_BusinessInfo_ErrorPayloadSchema>;
  DirectSeller_Documents_Completed: z.infer<typeof DirectSeller_Documents_CompletedPayloadSchema>;
  DirectSeller_SetupCompleted: z.infer<typeof DirectSeller_SetupCompletedPayloadSchema>;
};

export const OnboardingEventSchemaMap = {
  Onboarding_WelcomeCTAClicked: Onboarding_WelcomeCTAClickedPayloadSchema,
  Onboarding_WelcomeOptOutClicked: Onboarding_WelcomeOptOutClickedPayloadSchema,
  Onboarding_Home_OtherProductsCreateProductClicked: Onboarding_Home_OtherProductsCreateProductClickedPayloadSchema,
  Onboarding_Home_ChecklistItemClicked: Onboarding_Home_ChecklistItemClickedPayloadSchema,
  Onboarding_Home_ChecklistCompleteSubmitProductClicked:
    Onboarding_Home_ChecklistCompleteSubmitProductClickedPayloadSchema,
  Onboarding_Home_ActiveProductCardCreateFirstProductClicked:
    Onboarding_Home_ActiveProductCardCreateFirstProductClickedPayloadSchema,
  DirectSeller_SignupLinkGenerated: DirectSeller_SignupLinkGeneratedPayloadSchema,
  DirectSeller_WelcomeCTAClicked: DirectSeller_WelcomeCTAClickedPayloadSchema,
  DirectSeller_WizardStarted: DirectSeller_WizardStartedPayloadSchema,
  DirectSeller_StepViewed: DirectSeller_StepViewedPayloadSchema,
  DirectSeller_WizardClosed: DirectSeller_WizardClosedPayloadSchema,
  DirectSeller_StripeConnected: DirectSeller_StripeConnectedPayloadSchema,
  DirectSeller_Connection_ProvidersSelected: DirectSeller_Connection_ProvidersSelectedPayloadSchema,
  DirectSeller_Connection_ProvidersConnected: DirectSeller_Connection_ProvidersConnectedPayloadSchema,
  DirectSeller_Connection_ProvidersError: DirectSeller_Connection_ProvidersErrorPayloadSchema,
  DirectSeller_Connection_SEPAChosen: DirectSeller_Connection_SEPAChosenPayloadSchema,
  DirectSeller_Connection_AvgPriceChosen: DirectSeller_Connection_AvgPriceChosenPayloadSchema,
  DirectSeller_Connection_SalesVolumeChosen: DirectSeller_Connection_SalesVolumeChosenPayloadSchema,
  DirectSeller_Connection_ProductsOver10KChosen: DirectSeller_Connection_ProductsOver10KChosenPayloadSchema,
  DirectSeller_LimitGuidanceShown: DirectSeller_LimitGuidanceShownPayloadSchema,
  DirectSeller_LimitEmailCopied: DirectSeller_LimitEmailCopiedPayloadSchema,
  DirectSeller_Email_ProviderSelected: DirectSeller_Email_ProviderSelectedPayloadSchema,
  DirectSeller_Email_PostmarkConfigured: DirectSeller_Email_PostmarkConfiguredPayloadSchema,
  DirectSeller_Email_SMTPConfigured: DirectSeller_Email_SMTPConfiguredPayloadSchema,
  DirectSeller_LegalDraftGenerated: DirectSeller_LegalDraftGeneratedPayloadSchema,
  DirectSeller_LegalDocumentsSaved: DirectSeller_LegalDocumentsSavedPayloadSchema,
  DirectSeller_BusinessInfo_Success: DirectSeller_BusinessInfo_SuccessPayloadSchema,
  DirectSeller_BusinessInfo_Error: DirectSeller_BusinessInfo_ErrorPayloadSchema,
  DirectSeller_Documents_Completed: DirectSeller_Documents_CompletedPayloadSchema,
  DirectSeller_SetupCompleted: DirectSeller_SetupCompletedPayloadSchema,
};
